/**
 * @typedef {Object} ComponentCreateDto
 * @property {string} componentCode
 * @property {string} componentName
 * @property {number} componentTypeId
 * @property {number} measureTypeId
 * @property {string} [description]
 * @property {number} [standardCost]
 * @property {number} [lastCost]
 * @property {number} [minStockLevel]
 * @property {number} [maxStockLevel]
 * @property {number} [reorderPoint]
 * @property {number} [leadTimeDays]
 * @property {string} [upc]
 * @property {string} [ean]
 * @property {string} [gtin]
 * @property {string} [manufacturerPartNumber]
 * @property {string} [customerPartNumber]
 */

/**
 * @typedef {Object} ComponentUpdateDto
 * @property {string} [componentCode]
 * @property {string} [componentName]
 * @property {number} [componentTypeId]
 * @property {number} [measureTypeId]
 * @property {string} [description]
 * @property {number} [standardCost]
 * @property {number} [lastCost]
 * @property {number} [minStockLevel]
 * @property {number} [maxStockLevel]
 * @property {number} [reorderPoint]
 * @property {number} [leadTimeDays]
 * @property {string} [upc]
 * @property {string} [ean]
 * @property {string} [gtin]
 * @property {string} [manufacturerPartNumber]
 * @property {string} [customerPartNumber]
 * @property {boolean} [isActive]
 */

/**
 * @typedef {Object} ComponentResponseDto
 * @property {number} componentId
 * @property {string} orgId
 * @property {string} componentCode
 * @property {string} componentName
 * @property {number} componentTypeId
 * @property {string} componentTypeName
 * @property {number} measureTypeId
 * @property {string} measureTypeName
 * @property {string} [description]
 * @property {number} [standardCost]
 * @property {number} [lastCost]
 * @property {number} [minStockLevel]
 * @property {number} [maxStockLevel]
 * @property {number} [reorderPoint]
 * @property {number} [leadTimeDays]
 * @property {string} [upc]
 * @property {string} [ean]
 * @property {string} [gtin]
 * @property {string} [manufacturerPartNumber]
 * @property {string} [customerPartNumber]
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 * @property {number} [currentInventory]
 * @property {number} [availableQuantity]
 * @property {boolean} needsReorder
 */

/**
 * @typedef {Object} ComponentListResponse
 * @property {ComponentResponseDto[]} components
 * @property {number} totalCount
 * @property {number} pageNumber
 * @property {number} pageSize
 * @property {number} totalPages
 */

/**
 * @typedef {Object} ComponentLookupDto
 * @property {number} componentId
 * @property {string} componentCode
 * @property {string} componentName
 * @property {string} componentTypeName
 * @property {string} [upc]
 * @property {boolean} isActive
 */

/**
 * @typedef {Object} CodeAvailabilityResponse
 * @property {string} code
 * @property {boolean} isAvailable
 * @property {string} [message]
 */

/**
 * @typedef {Object} BatchImportResponse
 * @property {number} successCount
 * @property {number} errorCount
 * @property {string[]} [errors]
 */

const BASE_PATH = 'v1/Component';

export default function (instance) {
  return {
    /**
     * Get all components with pagination
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive=false]
     * @param {number} [params.pageNumber=1]
     * @param {number} [params.pageSize=50]
     * @returns {Promise<{data: ComponentListResponse}>}
     */
    getComponents(params = {}) {
      return instance.get(BASE_PATH, { params });
    },

    /**
     * Get component by ID
     * @param {number} id
     * @returns {Promise<{data: ComponentResponseDto}>}
     */
    getComponent(id) {
      return instance.get(`${BASE_PATH}/${id}`);
    },

    /**
     * Get component by code
     * @param {string} code
     * @returns {Promise<{data: ComponentResponseDto}>}
     */
    getComponentByCode(code) {
      return instance.get(`${BASE_PATH}/by-code/${encodeURIComponent(code)}`);
    },

    /**
     * Get component by UPC barcode
     * @param {string} upc
     * @returns {Promise<{data: ComponentResponseDto}>}
     */
    getComponentByUpc(upc) {
      return instance.get(`${BASE_PATH}/by-upc/${encodeURIComponent(upc)}`);
    },

    /**
     * Search components by name or code
     * @param {Object} [params]
     * @param {string} [params.query]
     * @param {number} [params.maxResults=50]
     * @returns {Promise<{data: ComponentResponseDto[]}>}
     */
    searchComponents(params = {}) {
      return instance.get(`${BASE_PATH}/search`, { params });
    },

    /**
     * Get component lookups for dropdowns
     * @param {Object} [params]
     * @param {number} [params.componentTypeId]
     * @returns {Promise<{data: ComponentLookupDto[]}>}
     */
    getComponentLookups(params = {}) {
      return instance.get(`${BASE_PATH}/lookups`, { params });
    },

    /**
     * Get components needing reorder
     * @returns {Promise<{data: ComponentResponseDto[]}>}
     */
    getComponentsNeedReorder() {
      return instance.get(`${BASE_PATH}/need-reorder`);
    },

    /**
     * Check if component code is available
     * @param {string} code
     * @param {Object} [params]
     * @param {number} [params.excludeId]
     * @returns {Promise<{data: CodeAvailabilityResponse}>}
     */
    checkCodeAvailability(code, params = {}) {
      return instance.get(`${BASE_PATH}/check-code/${encodeURIComponent(code)}`, { params });
    },

    /**
     * Get a suggested, currently-unused component code
     * @returns {Promise<{data: {componentCode: string}}>}
     */
    getNextCode() {
      return instance.get(`${BASE_PATH}/next-code`);
    },

    /**
     * Copy a component into a new one
     * @param {number} id
     * @param {Object} [params]
     * @param {string} [params.newCode]
     * @param {string} [params.newName]
     * @returns {Promise<{data: ComponentResponseDto}>}
     */
    copyComponent(id, params = {}) {
      return instance.post(`${BASE_PATH}/${id}/copy`, null, { params });
    },

    /**
     * Create new component
     * @param {ComponentCreateDto} payload
     * @returns {Promise<{data: ComponentResponseDto}>}
     */
    createComponent(payload) {
      return instance.post(BASE_PATH, payload);
    },

    /**
     * Update component
     * @param {number} id
     * @param {ComponentUpdateDto} payload
     * @returns {Promise<{data: ComponentResponseDto}>}
     */
    updateComponent(id, payload) {
      return instance.put(`${BASE_PATH}/${id}`, payload);
    },

    /**
     * Delete component (soft delete)
     * @param {number} id
     * @returns {Promise<void>}
     */
    deleteComponent(id) {
      return instance.delete(`${BASE_PATH}/${id}`);
    },

    /**
     * Batch import components
     * @param {ComponentCreateDto[]} components
     * @returns {Promise<{data: BatchImportResponse}>}
     */
    batchImport(components) {
      return instance.post(`${BASE_PATH}/batch-import`, components);
    }
  };
}
